import classNames from 'classnames';
import useWorldTheme from 'hooks/shared/useWorldTheme';
import type { FC } from 'react';
import { getTracingEvents } from 'utils/amplitude';
import type { RadioGroupsProps } from './RadioGroups.types';
import { customRadioGroupsTypeMap } from './RadioGroups.types';

const baseBorderTheme =
  'transition-colors transition-shadow border-transparent allow-focus-shadow data-[checked=true]:ring-offset-0 fancy-shadow-neutral';

const borderThemes = {
  bank: `${baseBorderTheme} data-[checked=true]:ring-bank-200 data-[checked=true]:border-bank`,
  expenses: `${baseBorderTheme} data-[checked=true]:ring-expenses-200 data-[checked=true]:border-expenses`,
  revenues: `${baseBorderTheme} data-[checked=true]:ring-revenues-200 data-[checked=true]:border-revenues`,
  taxes: `${baseBorderTheme} data-[checked=true]:ring-taxes-200 data-[checked=true]:border-taxes`,
  primary: `${baseBorderTheme} data-[checked=true]:ring-primary-200 data-[checked=true]:border-primary`,
  disabled: `border-transparent fancy-shadow-neutral`,
};

const baseInputTheme =
  'transition-colors transition-shadow border-transparent focus:ring-1 allow-focus-shadow focus:ring-offset-0 fancy-shadow-neutral'; // shadow-[0px_1px_2px_0px_rgba(53,26,77,0.03)]

const inputThemes = {
  bank: `${baseInputTheme} text-bank focus:ring-bank-200 focus:border-bank`,
  expenses: `${baseInputTheme} text-expenses focus:ring-expenses-200 focus:border-expenses`,
  revenues: `${baseInputTheme} text-revenues focus:ring-revenues-200 focus:border-revenues`,
  taxes: `${baseInputTheme} text-taxes focus:ring-taxes-200 focus:border-taxes`,
  primary: `${baseInputTheme} text-primary focus:ring-primary-200 focus:border-primary`,
  disabled: `border-transparent fancy-shadow-neutral text-primary-400 cursor-not-allowed`,
};

const RadioGroups: FC<RadioGroupsProps> = ({
  type,
  legendLabel = 'Radio Group Options',
  data,
  onChange,
  labelClassNames = 'text-sm text-gray-700',
  inputClassNames,
  className,
  tracing,
  descriptionClassNames,
  getOptionContainerClasses,
}) => {
  const { inlineClasses } =
    customRadioGroupsTypeMap[type] || customRadioGroupsTypeMap.default;

  const theme = useWorldTheme();

  const tracingEvents = getTracingEvents(tracing, { onChange });

  return (
    <fieldset>
      <legend className="sr-only">{legendLabel}</legend>
      <div className={classNames('space-y-2', inlineClasses, className)}>
        {data.map(
          ({
            id,
            title,
            description,
            checked,
            onClick,
            InfoComponent = () => null,
            disabled,
          }) => (
            <label
              key={String(id)}
              onClick={onClick}
              data-checked={!!checked}
              className={classNames(
                'items-center flex',
                disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                getOptionContainerClasses?.(
                  { checked: !!checked, disabled: !!disabled },
                  {
                    borderTheme: disabled
                      ? borderThemes.disabled
                      : borderThemes[theme],
                  },
                ),
              )}
            >
              <input
                type="radio"
                checked={!!checked}
                disabled={!!disabled}
                data-checked={!!checked}
                onChange={() => !disabled && tracingEvents.onChange(id)}
                className={classNames(
                  'h-4 w-4',
                  disabled ? inputThemes.disabled : inputThemes[theme],
                  inputClassNames,
                )}
              />

              <span
                className={classNames(
                  'pl-3 flex-1 block font-medium',
                  labelClassNames,
                )}
              >
                <p className={classNames('text-primary')}>{title}</p>
                {description && (
                  <p
                    className={classNames(
                      'text-sm font-medium',
                      descriptionClassNames,
                    )}
                  >
                    {description}
                  </p>
                )}
              </span>

              <InfoComponent />
            </label>
          ),
        )}
      </div>
    </fieldset>
  );
};

export default RadioGroups;
